import { classifyFetchFailure, parseRetryAfter, TransportError, type FetcherKind } from './errors';
import { maybeGunzip } from '../bytes/gzip';

export interface FetchInit {
  method?: 'GET' | 'POST';
  body?: string | Uint8Array;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface FetchResult {
  url: string;
  status: number;
  headers: Headers;
  /** Response body; gzip is inflated even when the server forgot Content-Encoding. */
  body: Uint8Array;
  via: FetcherKind;
  /** ms to wait before retrying, when the server said so (429/503). */
  retryAfterMs?: number;
}

/** A way of performing an HTTP request. Network-level failures throw TransportError; HTTP errors are returned. */
export interface Fetcher {
  readonly kind: FetcherKind;
  fetch(url: string, init?: FetchInit): Promise<FetchResult>;
}

export async function toFetchResult(res: Response, url: string, via: FetcherKind): Promise<FetchResult> {
  return fetchResultFrom(url, res.status, res.headers, new Uint8Array(await res.arrayBuffer()), via);
}

/** Builds a FetchResult from raw parts (the desktop shell has no Response object). */
export async function fetchResultFrom(url: string, status: number, headers: Headers | Record<string, string>, bytes: Uint8Array, via: FetcherKind): Promise<FetchResult> {
  const h = headers instanceof Headers ? headers : new Headers(headers);
  const body = await maybeGunzip(bytes);
  const out: FetchResult = { url, status, headers: h, body, via };
  const ra = parseRetryAfter(h.get('retry-after'));
  if (ra !== undefined) out.retryAfterMs = ra;
  return out;
}

function toRequestInit(init?: FetchInit): RequestInit {
  const ri: RequestInit = { method: init?.method ?? 'GET' };
  if (init?.headers) ri.headers = init.headers;
  if (init?.body !== undefined) ri.body = init.body as BodyInit;
  if (init?.signal) ri.signal = init.signal;
  return ri;
}

/** Result of the no-cors reachability probe, per origin, for a short while. */
const probeCache = new Map<string, { reachable: boolean; at: number }>();
const PROBE_TTL_MS = 60_000;

/**
 * Tells a CORS rejection apart from a dead host. A `no-cors` request ignores CORS, so it resolves
 * (opaquely) whenever the server answered at all and rejects only on DNS / connection / TLS failure.
 */
export async function probeReachable(url: string, fetchFn: typeof fetch = fetch): Promise<boolean> {
  let origin: string;
  try {
    origin = new URL(url).origin;
  } catch {
    return false;
  }
  const cached = probeCache.get(origin);
  if (cached && Date.now() - cached.at < PROBE_TTL_MS) return cached.reachable;
  let reachable: boolean;
  try {
    const res = await fetchFn(url, { mode: 'no-cors', method: 'GET', cache: 'no-store', redirect: 'follow' });
    // Drain the (opaque) body: an unconsumed response keeps the loader alive in the renderer, and
    // Blink's request scheduler then holds back later requests from the same page.
    try {
      await res.arrayBuffer();
    } catch {
      /* opaque bodies may be unreadable; the response itself is what we needed */
    }
    reachable = true;
  } catch {
    reachable = false;
  }
  probeCache.set(origin, { reachable, at: Date.now() });
  return reachable;
}

/** Like classifyFetchFailure, but a browser TypeError is resolved into `cors-blocked` vs `dns` by probing. */
export async function classifyDirectFailure(e: unknown, url: string, fetchFn: typeof fetch = fetch): Promise<TransportError> {
  const err = classifyFetchFailure(e, url, 'direct');
  if (err.kind !== 'cors-blocked') return err;
  if (await probeReachable(url, fetchFn)) return err;
  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    /* keep url */
  }
  return new TransportError('dns', `${host} could not be reached (name resolution or connection failed)`, { url, via: 'direct', cause: e });
}

/** Plain browser fetch; subject to CORS. */
export const directFetcher: Fetcher = {
  kind: 'direct',
  async fetch(url, init) {
    try {
      const res = await fetch(url, toRequestInit(init));
      return await toFetchResult(res, url, 'direct');
    } catch (e) {
      throw await classifyDirectFailure(e, url);
    }
  },
};

/**
 * Routes through a CORS proxy. `base` may contain `{url}` (replaced with the encoded target),
 * otherwise `?url=<encoded>` is appended.
 */
export function proxyFetcher(base: string): Fetcher {
  const build = (url: string): string => {
    const enc = encodeURIComponent(url);
    if (base.includes('{url}')) return base.replace('{url}', enc);
    const sep = base.includes('?') ? (base.endsWith('?') || base.endsWith('&') ? '' : '&') : '?';
    return `${base}${sep}url=${enc}`;
  };
  return {
    kind: 'proxy',
    async fetch(url, init) {
      const target = build(url);
      try {
        const res = await fetch(target, toRequestInit(init));
        return await toFetchResult(res, url, 'proxy');
      } catch (e) {
        const err = classifyFetchFailure(e, target, 'proxy');
        throw err.kind === 'cors-blocked' ? err : err;
      }
    },
  };
}
